import React, { useMemo } from 'react';
import Select from 'react-select';
import CardView from './CardView';

interface ListViewProps {
  data: any[];
  groupBy: string;
  setGroupBy: (value: string) => void;
  quickFilterText: string;
  language: 'fa' | 'en';
  isDark: boolean;
  isRTL: boolean;
  translations: any;
}

interface GroupByOption {
  value: string;
  label: string;
}

const ListView: React.FC<ListViewProps> = ({ 
  data, 
  groupBy, 
  setGroupBy, 
  quickFilterText, 
  language, 
  isDark, 
  isRTL, 
  translations 
}) => {
  // Group By options for react-select
  const groupByOptions = useMemo<GroupByOption[]>(() => [
    { value: 'intent', label: language === 'fa' ? 'نیت' : 'Intent' },
    { value: 'difficulty', label: language === 'fa' ? 'سختی' : 'Difficulty' },
    { value: 'searchVolume', label: language === 'fa' ? 'حجم جستجو' : 'Search Volume' },
    { value: 'length', label: language === 'fa' ? 'طول کلمه' : 'Length' },
    { value: 'purpose', label: language === 'fa' ? 'هدف' : 'Purpose' },
    { value: 'source', label: language === 'fa' ? 'منبع' : 'Source' },
  ], [language]);

  // Custom styles for react-select
  const customStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      borderColor: isDark ? '#4b5563' : '#d1d5db',
      color: isDark ? '#f3f4f6' : '#111827',
      minHeight: '42px',
      '&:hover': {
        borderColor: '#6e46e5',
      },
      boxShadow: state.isFocused ? '0 0 0 2px rgba(110, 70, 229, 0.2)' : 'none',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      maxHeight: '300px',
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '300px',
      padding: '4px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#6e46e5'
        : state.isFocused
        ? isDark ? '#374151' : '#f3f4f6'
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : isDark ? '#f3f4f6' : '#111827',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#6e46e5',
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    input: (base: any) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? '#9ca3af' : '#6b7280',
    }),
  }), [isDark]);

  return (
    <div className="w-full">
      {/* Group By Selector */}
      <div className={`mb-4 flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
        {!isRTL && (
          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
          </label>
        )}
        <div style={{ minWidth: '200px' }}>
          <Select<GroupByOption>
            value={groupByOptions.find(option => option.value === groupBy)}
            onChange={(selectedOption) => {
              setGroupBy(selectedOption?.value || 'intent');
            }}
            options={groupByOptions}
            isSearchable={false}
            isClearable={false}
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
            theme={(selectTheme) => ({
              ...selectTheme,
              colors: {
                ...selectTheme.colors,
                primary: '#6e46e5',
                primary75: '#8b6ce8',
                primary50: '#a894eb',
                primary25: '#c5b8ee',
              },
            })}
          />
        </div>
        {isRTL && (
          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {language === 'fa' ? 'گروه‌بندی بر اساس:' : 'Group by:'}
          </label>
        )}
      </div>

      {/* List Groups */}
      <CardView 
        data={data}
        groupBy={groupBy}
        quickFilterText={quickFilterText}
        language={language}
        isDark={isDark}
        isRTL={isRTL}
        translations={translations}
        viewType="list"
      />
    </div>
  );
};

export default ListView;


